import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, Loader2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { format, subMonths, subYears } from 'date-fns';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Banner } from '@/components/ui/Banner';
import { useAuthStore } from '@/stores/auth';
import { kpiConsolidesApi } from '../api';
import { FiltersModal } from '../components/FiltersModal';
import { StatCard } from '../components/StatCard';
import { SouscriptionsArea } from '../components/SouscriptionsArea';
import { TopCompagnies } from '../components/TopCompagnies';
import { VentilationApporteurs } from '../components/VentilationApporteurs';
import { TableauCroiseCompagnieMois } from '../components/TableauCroiseCompagnieMois';
import { buildComparisonData, computeProrataToYearEnd, getGranularity } from '../lib/chart-utils';
import type { ApporteurStats, DateType, FilterState, SouscriptionEntry } from '../types';

/**
 * Dashboard souscriptions (API métier externe). Admin + director.
 *
 * Deux modes d'utilisation :
 *
 *  1. **Standalone** via `KpiConsolidesPage` : page autonome avec
 *     AppLayout + PageHeader + bouton Filtres (Période + Compagnies).
 *     Accessible via la route redirector `/kpi-consolides` →
 *     `/kpi?tab=adelie` (pour rétrocompatibilité bookmarks).
 *
 *  2. **Embeddable** via `KpiConsolidesContent` : composant content-only
 *     qui reçoit `dateFrom` + `dateTo` en props depuis le parent. Utilisé
 *     dans le shell `KpiDirectionPage` sous l'onglet « Adélie » — le
 *     filtre Période est hérité du parent, seules les Compagnies restent
 *     ajustables via la modale locale (+ le toggle Date souscription /
 *     Date d'effet).
 *
 * - 3 appels API en parallèle (N, N-1, apporteurs) via cache client 20 min
 * - `loadId` ref pour ignorer les réponses obsolètes si l'utilisateur
 *   change de filtres rapidement
 * - Bannière d'erreur si l'API métier n'est pas configurée (503 backend)
 */

function defaultFilters(): FilterState {
  const today = new Date();
  return {
    companies: [],
    dateFrom: format(subMonths(today, 12), 'yyyy-MM-dd'),
    dateTo: format(today, 'yyyy-MM-dd'),
  };
}

function prevYearRange(dateFrom: string, dateTo: string): { prevFrom: string; prevTo: string } {
  return {
    prevFrom: format(subYears(new Date(dateFrom), 1), 'yyyy-MM-dd'),
    prevTo: format(subYears(new Date(dateTo), 1), 'yyyy-MM-dd'),
  };
}

// ---------------------------------------------------------------------------
// Bouton « Filtres » — style Stripe secondary, badge si compagnies actives.
// ---------------------------------------------------------------------------
function FiltersButton({ activeCount, onClick }: { activeCount: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border border-stripe-border bg-white px-3 py-1.5 text-2xs font-medium text-stripe-text transition-colors hover:bg-stripe-hover"
    >
      <Filter className="h-3.5 w-3.5 text-stripe-text-muted" />
      Filtres
      {activeCount > 0 && (
        <span className="ml-1 rounded-full bg-stripe-accent-bg px-1.5 py-0.5 text-[10px] font-semibold text-stripe-accent">
          {activeCount}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Toggle Date de souscription / Date d'effet — design Stripe segmented.
// ---------------------------------------------------------------------------
function DateTypeToggle({ value, onChange }: { value: DateType; onChange: (v: DateType) => void }) {
  return (
    <div className="inline-flex items-center rounded-md border border-stripe-border bg-white text-2xs">
      <button
        type="button"
        onClick={() => onChange('souscription')}
        className={`rounded-l-md px-3 py-1.5 font-medium ${
          value === 'souscription'
            ? 'bg-stripe-accent text-white'
            : 'text-stripe-text-secondary hover:bg-stripe-hover'
        }`}
      >
        Date de souscription
      </button>
      <button
        type="button"
        onClick={() => onChange('effet')}
        className={`rounded-r-md px-3 py-1.5 font-medium ${
          value === 'effet'
            ? 'bg-stripe-accent text-white'
            : 'text-stripe-text-secondary hover:bg-stripe-hover'
        }`}
      >
        Date d&apos;effet
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variante EMBEDDABLE (utilisée par le tab Adélie de KpiDirectionPage).
// Reçoit dateFrom / dateTo depuis le parent. Garde en state local :
//   - filters.companies (modale)
//   - dateType (toggle Date souscription / Date effet)
//   - filtersOpen (ouverture modale)
// Plus de sidebar : la grille graphs prend toute la largeur.
// ---------------------------------------------------------------------------
export interface KpiConsolidesContentProps {
  dateFrom: string;
  dateTo: string;
}

export function KpiConsolidesContent({ dateFrom, dateTo }: KpiConsolidesContentProps) {
  const token = useAuthStore((s) => s.accessToken)!;

  const [companies, setCompanies] = useState<string[]>([]);
  const [dateType, setDateType] = useState<DateType>('souscription');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // On charge systématiquement DEUX séries en parallèle (pivot souscription
  // et pivot effet) pour que chaque KPI puisse être calculé depuis la source
  // qui matche sa sémantique métier, indépendamment du toggle UI :
  //  - 4 StatCards haut + graphe area + top compagnies + tableau croisé →
  //    suivent le toggle (entriesByToggle)
  //  - AFN Cotisation prorata N → toujours depuis `currEffet` (contrats
  //    dont la prise d'effet tombe dans la période)
  //  - Frais encaissés + Rétrocommissions → toujours depuis `currSousc`
  //    (frais payés à la date de souscription)
  //
  // Coût : 4 appels contrats au lieu de 2, mais ils tapent tous le même
  // cache serveur `rawContratsCache` → un seul fetch API externe par
  // tranche de 15 min. Le cache client absorbe le toggle instantanément.
  const [currSousc, setCurrSousc] = useState<SouscriptionEntry[] | null>(null);
  const [prevSousc, setPrevSousc] = useState<SouscriptionEntry[] | null>(null);
  const [currEffet, setCurrEffet] = useState<SouscriptionEntry[] | null>(null);
  const [prevEffet, setPrevEffet] = useState<SouscriptionEntry[] | null>(null);
  const [apporteurStats, setApporteurStats] = useState<ApporteurStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadIdRef = useRef(0);

  const load = useCallback(async () => {
    const myLoadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);
    const { prevFrom, prevTo } = prevYearRange(dateFrom, dateTo);
    const companyParam = companies.length > 0 ? companies : undefined;
    try {
      const [cs, ps, ce, pe, apporteurs] = await Promise.all([
        kpiConsolidesApi.souscriptions(token, {
          company: companyParam,
          dateType: 'souscription',
          dateFrom,
          dateTo,
        }),
        kpiConsolidesApi.souscriptions(token, {
          company: companyParam,
          dateType: 'souscription',
          dateFrom: prevFrom,
          dateTo: prevTo,
        }),
        kpiConsolidesApi.souscriptions(token, {
          company: companyParam,
          dateType: 'effet',
          dateFrom,
          dateTo,
        }),
        kpiConsolidesApi.souscriptions(token, {
          company: companyParam,
          dateType: 'effet',
          dateFrom: prevFrom,
          dateTo: prevTo,
        }),
        kpiConsolidesApi.apporteurs(token, {
          company: companyParam,
          dateType,
          dateFrom,
          dateTo,
        }),
      ]);
      if (myLoadId !== loadIdRef.current) return;
      setCurrSousc(cs);
      setPrevSousc(ps);
      setCurrEffet(ce);
      setPrevEffet(pe);
      setApporteurStats(apporteurs);
    } catch (err) {
      if (myLoadId !== loadIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Erreur');
      setCurrSousc([]);
      setPrevSousc([]);
      setCurrEffet([]);
      setPrevEffet([]);
      setApporteurStats(null);
    } finally {
      if (myLoadId === loadIdRef.current) setLoading(false);
    }
  }, [token, companies, dateType, dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  // Collection qui matche le toggle — alimente les visualisations globales
  // (graphe area N/N-1, top compagnies, tableau croisé, 4 StatCards haut).
  const currentEntries = dateType === 'effet' ? currEffet : currSousc;
  const prevEntries = dateType === 'effet' ? prevEffet : prevSousc;

  const kpis = useMemo(() => {
    if (!currentEntries || !prevEntries) return null;
    if (!currSousc || !prevSousc || !currEffet || !prevEffet) return null;

    // ---- KPIs qui SUIVENT le toggle (visualisations globales) ----
    const totalN = currentEntries.length;
    const totalN1 = prevEntries.length;
    const variation = totalN1 === 0 ? null : ((totalN - totalN1) / totalN1) * 100;

    const byCompany = new Map<string, number>();
    for (const e of currentEntries) byCompany.set(e.company, (byCompany.get(e.company) ?? 0) + 1);
    const sortedCompanies = Array.from(byCompany.entries()).sort((a, b) => b[1] - a[1]);
    const topCompany = sortedCompanies[0] ?? ['—', 0];

    const dateField = dateType === 'effet' ? 'dateEffet' : 'dateSouscription';
    const uniqueDays = new Set(currentEntries.map((e) => e[dateField])).size;

    // ---- KPIs financiers — SÉMANTIQUE FIXE (insensibles au toggle) ----

    // AFN Cotisation prorata N → TOUJOURS les contrats dont la prise d'effet
    // tombe dans la période (Affaires Nouvelles de l'année N). On ne somme
    // donc pas sur `currentEntries` mais sur `currEffet` quel que soit le
    // toggle UI.
    const totalProrata = currEffet.reduce(
      (s, e) => s + computeProrataToYearEnd(e.dateEffet, e.cotisationAnnuelleTTC),
      0,
    );
    const totalProrataN1 = prevEffet.reduce(
      (s, e) => s + computeProrataToYearEnd(e.dateEffet, e.cotisationAnnuelleTTC),
      0,
    );
    const variationProrata =
      totalProrataN1 === 0 ? null : ((totalProrata - totalProrataN1) / totalProrataN1) * 100;

    // Frais encaissés + Rétrocommissions → TOUJOURS depuis `currSousc` car
    // la règle métier est "frais / rétro payés à la date de souscription",
    // indépendamment de la date d'effet du contrat.
    const totalFrais = currSousc.reduce((s, e) => s + e.fraisPaid, 0);
    const totalFraisN1 = prevSousc.reduce((s, e) => s + e.fraisPaid, 0);
    const variationFrais =
      totalFraisN1 === 0 ? null : ((totalFrais - totalFraisN1) / totalFraisN1) * 100;

    const totalRetro = currSousc.reduce((s, e) => s + e.retrocommissionPaid, 0);
    const totalRetroN1 = prevSousc.reduce((s, e) => s + e.retrocommissionPaid, 0);
    const variationRetro =
      totalRetroN1 === 0 ? null : ((totalRetro - totalRetroN1) / totalRetroN1) * 100;

    const totalFraisNetRetro = totalFrais - totalRetro;

    return {
      totalN,
      variation,
      topCompany,
      uniqueCompanies: byCompany.size,
      uniqueDays,
      totalFrais,
      totalFraisNetRetro,
      variationFrais,
      totalProrata,
      variationProrata,
      totalRetro,
      variationRetro,
    };
  }, [currentEntries, prevEntries, currSousc, prevSousc, currEffet, prevEffet, dateType]);

  const areaData = useMemo(() => {
    if (!currentEntries || !prevEntries) return [];
    const g = getGranularity(dateFrom, dateTo);
    const dateField = dateType === 'effet' ? 'dateEffet' : 'dateSouscription';
    return buildComparisonData(currentEntries, prevEntries, g, dateField);
  }, [currentEntries, prevEntries, dateFrom, dateTo, dateType]);

  const granularity = useMemo(() => getGranularity(dateFrom, dateTo), [dateFrom, dateTo]);

  // FilterState local (pour compat avec `FiltersModal` qui attend cette shape).
  // Les dates ne sont pas modifiables ici — la section Période est masquée via
  // `hidePeriodSection`.
  const filterState: FilterState = { companies, dateFrom, dateTo };
  const defaultsForReset: FilterState = { companies: [], dateFrom, dateTo };

  return (
    <div className="space-y-5">
      {/* Barre de commandes : bouton Filtres à gauche du toggle. Plus de
          label « Vue par » — le toggle est self-explicit. */}
      <div className="flex items-center justify-end gap-3">
        <FiltersButton activeCount={companies.length} onClick={() => setFiltersOpen(true)} />
        <DateTypeToggle value={dateType} onChange={setDateType} />
      </div>

      {error && (
        <Banner tone="danger">
          <span className="font-medium">Chargement impossible</span> · {error}
        </Banner>
      )}

      {loading && !currentEntries && !error && (
        <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-stripe border border-stripe-border bg-white py-16 shadow-stripe-sm">
          <Loader2 className="h-6 w-6 animate-spin text-stripe-accent" />
          <p className="text-sm font-medium text-stripe-text">Chargement des données…</p>
          <p className="max-w-md text-center text-2xs text-stripe-text-muted">
            Le premier chargement peut prendre jusqu&apos;à 15 secondes (~48 000 contrats depuis
            l&apos;API métier). Les consultations suivantes seront instantanées pendant 15 minutes.
          </p>
        </div>
      )}

      {currentEntries && kpis && (
        <>
          <div className="stagger grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              label="Total souscriptions"
              value={kpis.totalN.toLocaleString('fr-FR')}
              sub="sur la période"
              variation={kpis.variation}
            />
            <StatCard
              label="1re compagnie"
              value={kpis.topCompany[0] as string}
              sub={`${(kpis.topCompany[1] as number).toLocaleString('fr-FR')} contrats`}
            />
            <StatCard
              label="Compagnies"
              value={kpis.uniqueCompanies.toLocaleString('fr-FR')}
              sub="avec souscriptions"
            />
            <StatCard
              label="Jours ouvrés"
              value={kpis.uniqueDays.toLocaleString('fr-FR')}
              sub="avec données"
            />
          </div>

          {/* Financier Adélie — 3 cards dans l'ordre : cotisation prorata
              (non liée au paiement), frais encaissés (statutPaiement > 0)
              avec sub « Net de rétrocommission », rétrocommissions
              (retroVersee > 0). */}
          <div className="stagger grid grid-cols-1 gap-3 lg:grid-cols-3">
            <StatCard
              label="AFN Cotisation prorata N"
              value={`${Math.round(kpis.totalProrata).toLocaleString('fr-FR')} €`}
              sub="Affaires nouvelles — prorata depuis la date d'effet"
              variation={kpis.variationProrata}
            />
            <StatCard
              label="Frais encaissés"
              value={`${Math.round(kpis.totalFrais).toLocaleString('fr-FR')} €`}
              sub={`Net de rétrocommission : ${Math.round(kpis.totalFraisNetRetro).toLocaleString('fr-FR')} €`}
              variation={kpis.variationFrais}
            />
            <StatCard
              label="Rétrocommissions"
              value={`${Math.round(kpis.totalRetro).toLocaleString('fr-FR')} €`}
              sub="versées aux apporteurs"
              variation={kpis.variationRetro}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <SouscriptionsArea data={areaData} granularity={granularity} dateType={dateType} />
            </div>
            <div>
              <TopCompagnies
                entries={currentEntries}
                dateField={dateType === 'effet' ? 'dateEffet' : 'dateSouscription'}
              />
            </div>
          </div>

          {apporteurStats && apporteurStats.total > 0 && (
            <VentilationApporteurs stats={apporteurStats} />
          )}

          <TableauCroiseCompagnieMois
            entries={currentEntries}
            dateField={dateType === 'effet' ? 'dateEffet' : 'dateSouscription'}
          />
        </>
      )}

      {filtersOpen && (
        <FiltersModal
          value={filterState}
          onChange={(next) => setCompanies(next.companies)}
          defaults={defaultsForReset}
          onClose={() => setFiltersOpen(false)}
          hidePeriodSection
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Standalone — utilisé UNIQUEMENT si on revenait à la route dédiée. En V1
// actuel, `/kpi-consolides` redirige vers `/kpi?tab=adelie`, donc ce wrapper
// n'est plus monté directement. Conservé pour rétrocompat et tests.
//
// Même UX que le tab Adélie : plus de sidebar, modale complète (Période +
// Compagnies) ouverte via le bouton Filtres.
// ---------------------------------------------------------------------------
export function KpiConsolidesPage() {
  const user = useAuthStore((s) => s.user);
  const isDirection = user?.role === 'admin' || user?.role === 'director';

  const defaults = useMemo(() => defaultFilters(), []);
  const [filters, setFilters] = useState<FilterState>(defaults);
  const [filtersOpen, setFiltersOpen] = useState(false);

  if (!isDirection) return <Navigate to="/" replace />;

  // Comptage filtres actifs (période + compagnies) pour le badge du bouton.
  const activeCount =
    filters.companies.length +
    (filters.dateFrom !== defaults.dateFrom ? 1 : 0) +
    (filters.dateTo !== defaults.dateTo ? 1 : 0);

  return (
    <AppLayout>
      <PageHeader
        title="KPI consolidés"
        subtitle="Souscriptions de l'API métier — cache serveur 15 min, mis à jour à la demande."
        actions={<FiltersButton activeCount={activeCount} onClick={() => setFiltersOpen(true)} />}
      />
      <KpiConsolidesContent dateFrom={filters.dateFrom} dateTo={filters.dateTo} />
      {filtersOpen && (
        <FiltersModal
          value={filters}
          onChange={setFilters}
          defaults={defaults}
          onClose={() => setFiltersOpen(false)}
        />
      )}
    </AppLayout>
  );
}
